export type TrackingIds = Record<string, string>;

export type LeadOriginHistoryEntry = {
  origin: string;
  timestamp: string;
  trackingIds: TrackingIds;
};

type LeadOriginResolverOptions = {
  searchParams: URLSearchParams;
  trackingIds: TrackingIds;
  referrer: string;
  hostname: string;
};

const TRACKING_ID_KEYS = [
  "gclid",
  "gbraid",
  "wbraid",
  "dclid",
  "fbclid",
  "ttclid",
  "msclkid",
  "twclid",
  "li_fat_id",
  "ltclid",
  // Google Ads auto-tagging campaign/source. When an ad lands directly on
  // the quote app (skipping topaesthetics.com), this is the only reliable
  // campaign signal that survives — Performance Max clicks often can't be
  // resolved via click_view but always carry gad_campaignid when
  // auto-tagging is enabled in Google Ads.
  "gad_source",
  "gad_campaignid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "utm_id",
];

const DEFAULT_COOKIE_DAYS = 30;
const LEAD_ORIGIN_FIRST_COOKIE = "lead_origin_first";
const LEAD_ORIGIN_LAST_COOKIE = "lead_origin_last";
const LEAD_ORIGIN_HISTORY_COOKIE = "lead_origin_history";
const LEAD_ORIGIN_LEGACY_COOKIE = "lead_origin";
const MAX_LEAD_ORIGIN_HISTORY = 20;

export const getCookie = (name: string): string | null => {
  const nameEQ = name + "=";
  const cookies = document.cookie.split(";");
  for (let cookie of cookies) {
    cookie = cookie.trim();
    if (cookie.indexOf(nameEQ) === 0) {
      return cookie.substring(nameEQ.length);
    }
  }
  return null;
};

// Cookies are scoped to `.topaesthetics.com` so the React app, the PHP
// marketing site, and the ps. MedSpa subdomain share tracking state. On
// non-topaesthetics hosts (localhost dev, preview deploys) we omit the
// domain attribute — otherwise the browser silently drops the cookie.
const getCookieDomainAttr = (): string => {
  if (typeof window === "undefined") return "";
  const host = window.location.hostname || "";
  return host.endsWith("topaesthetics.com") ? "; domain=.topaesthetics.com" : "";
};

export const setCookie = (
  name: string,
  value: string,
  days = DEFAULT_COOKIE_DAYS,
) => {
  const maxAge = days * 24 * 60 * 60;
  document.cookie = `${name}=${value}; path=/; max-age=${maxAge}; samesite=lax${getCookieDomainAttr()}`;
};

const deleteCookie = (name: string) => {
  // Must match the domain attribute used at write time; otherwise the
  // browser treats it as a different cookie and leaves the original.
  document.cookie = `${name}=; path=/; max-age=0${getCookieDomainAttr()}`;
};

// Tracking values are stored in **sessionStorage** — scoped to the current
// browsing session and cleared when the tab/browser closes.
//
// This is deliberate: a tracking id (especially the gclid) must NOT outlive the
// session and get attached to a *later* visitor — e.g. a stale gclid baked into
// a shared link, or a reused/coordinator browser — or one ad click smears
// across many patients (the gclid-reuse bug, see docs/marketing/GCLID_GUARD.md).
// Within a session it still carries across the multi-step funnel.
//
// Old long-lived localStorage / `.topaesthetics.com` cookies (from before this
// change) are intentionally NOT read — they are ignored and expire on their own;
// setStoredValue also actively deletes any stale cookie of the same name. The
// cross-surface (PHP marketing site → funnel) hand-off happens via the URL.
export const getStoredValue = (name: string): string | null => {
  try {
    return window.sessionStorage.getItem(name);
  } catch {
    // sessionStorage unavailable (private mode) — attribution simply won't
    // carry across steps this visit; never falls back to long-lived storage.
    return null;
  }
};

export const setStoredValue = (name: string, value: string) => {
  try {
    window.sessionStorage.setItem(name, value);
  } catch {
    // sessionStorage unavailable — drop silently rather than persist long-lived.
  }
  // Belt-and-suspenders: clear any stale long-lived cookie so it can't be
  // read by legacy code paths or leak across sessions.
  deleteCookie(name);
};

const safeDecode = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const normalizeOriginToken = (value: string): string => {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");
};

export const readTrackingIdsCookie = (): TrackingIds => {
  const raw = getStoredValue("tracking_ids");
  if (!raw) return {};

  try {
    const parsed = JSON.parse(safeDecode(raw));
    if (!parsed || typeof parsed !== "object") return {};

    const result: TrackingIds = {};
    Object.entries(parsed).forEach(([key, value]) => {
      if (typeof value === "string" && value) {
        result[key] = value;
      }
    });
    return result;
  } catch {
    return {};
  }
};

export const readLeadOriginHistoryCookie = (): LeadOriginHistoryEntry[] => {
  const raw = getStoredValue(LEAD_ORIGIN_HISTORY_COOKIE);
  if (!raw) return [];

  try {
    const parsed = JSON.parse(safeDecode(raw));
    if (!Array.isArray(parsed)) return [];

    return parsed.filter((entry) => {
      return (
        entry &&
        typeof entry.origin === "string" &&
        typeof entry.timestamp === "string" &&
        entry.trackingIds &&
        typeof entry.trackingIds === "object"
      );
    }) as LeadOriginHistoryEntry[];
  } catch {
    return [];
  }
};

export const serializeTrackingIds = (trackingIds: TrackingIds): string => {
  return encodeURIComponent(JSON.stringify(trackingIds));
};

export const serializeLeadOriginHistory = (
  history: LeadOriginHistoryEntry[],
): string => {
  return encodeURIComponent(JSON.stringify(history));
};

export const extractTrackingIdsFromSearchParams = (
  searchParams: URLSearchParams,
): TrackingIds => {
  const result: TrackingIds = {};
  TRACKING_ID_KEYS.forEach((key) => {
    const value = searchParams.get(key);
    if (value) {
      result[key] = value;
    }
  });
  return result;
};

export const mergeTrackingIds = (
  ...sources: Array<TrackingIds | null | undefined>
): TrackingIds => {
  const merged: TrackingIds = {};
  sources.forEach((source) => {
    if (!source) return;
    Object.entries(source).forEach(([key, value]) => {
      if (value) {
        merged[key] = value;
      }
    });
  });
  return merged;
};

export const resolveLeadOrigin = ({
  searchParams,
  trackingIds,
  referrer,
  hostname,
}: LeadOriginResolverOptions): string | null => {
  const leadOriginParam = searchParams.get("lead_origin")?.trim();
  if (leadOriginParam) {
    return leadOriginParam;
  }

  if (
    trackingIds.gclid ||
    trackingIds.gbraid ||
    trackingIds.wbraid ||
    trackingIds.dclid
  ) {
    return "google_ads";
  }

  const utmSource = searchParams.get("utm_source")?.trim();
  const utmMedium = searchParams.get("utm_medium")?.trim();
  const normalizedSource = utmSource ? normalizeOriginToken(utmSource) : "";
  const normalizedMedium = utmMedium ? normalizeOriginToken(utmMedium) : "";
  const referrerLower = referrer.toLowerCase();

  const isPaidSocialMedium =
    normalizedMedium === "cpc" ||
    normalizedMedium === "paid" ||
    normalizedMedium === "paid-social" ||
    normalizedMedium === "paidsocial" ||
    normalizedMedium === "ads" ||
    normalizedMedium === "paid-ads";
  const isMetaSource =
    normalizedSource === "facebook" ||
    normalizedSource === "fb" ||
    normalizedSource === "meta" ||
    normalizedSource === "meta-ads" ||
    normalizedSource === "meta_ads";
  const isInstagramSource =
    normalizedSource.includes("instagram") || normalizedSource === "ig";

  if (
    trackingIds.fbclid ||
    isMetaSource ||
    (isInstagramSource && isPaidSocialMedium)
  ) {
    return "meta_ads";
  }

  if (
    isInstagramSource ||
    normalizedMedium.includes("instagram") ||
    normalizedMedium === "ig" ||
    referrerLower.includes("instagram.com")
  ) {
    return "instagram";
  }

  if (
    trackingIds.ltclid ||
    referrerLower.includes("linktr.ee")
  ) {
    return "linktree";
  }

  if (normalizedSource) {
    // Self-referral (e.g. utm_source=topaesthetics.com from own iframe embed)
    // should be treated as "direct", not a real lead origin
    if (hostname && normalizedSource === normalizeOriginToken(hostname)) {
      return "direct";
    }
    return normalizedSource;
  }

  if (normalizedMedium) {
    return normalizedMedium;
  }

  if (!referrerLower || (hostname && referrerLower.includes(hostname))) {
    return "direct";
  }

  return "direct";
};

export const persistTrackingFromUrl = (
  search: string,
  referrer: string,
  hostname: string,
) => {
  const searchParams = new URLSearchParams(search);
  const existingTrackingIds = readTrackingIdsCookie();
  const legacyGclid = getStoredValue("gclid");
  const incomingTrackingIds = extractTrackingIdsFromSearchParams(searchParams);
  const hasIncomingTrackingIds = Object.keys(incomingTrackingIds).length > 0;
  const mergedTrackingIds = mergeTrackingIds(
    existingTrackingIds,
    legacyGclid ? { gclid: legacyGclid } : undefined,
    incomingTrackingIds,
  );

  if (Object.keys(mergedTrackingIds).length > 0) {
    setStoredValue("tracking_ids", serializeTrackingIds(mergedTrackingIds));
    if (mergedTrackingIds.gclid) {
      setStoredValue("gclid", mergedTrackingIds.gclid);
    }
  }

  const existingLeadOrigin = getStoredValue(LEAD_ORIGIN_LEGACY_COOKIE);
  const existingLeadOriginFirst = getStoredValue(LEAD_ORIGIN_FIRST_COOKIE);
  const existingLeadOriginLast = getStoredValue(LEAD_ORIGIN_LAST_COOKIE);
  const resolvedLeadOrigin = resolveLeadOrigin({
    searchParams,
    trackingIds: hasIncomingTrackingIds ? incomingTrackingIds : mergedTrackingIds,
    referrer,
    hostname,
  });

  const hasExplicitLeadOrigin = Boolean(searchParams.get("lead_origin"));
  // "direct" should never override an existing real lead origin —
  // only meta, google, sms, etc. should override
  const isDirectOrigin = resolvedLeadOrigin === "direct";
  const hasExistingRealOrigin =
    Boolean(existingLeadOriginLast) && existingLeadOriginLast !== "direct";
  const shouldUpdateLeadOrigin =
    Boolean(resolvedLeadOrigin) &&
    (hasExplicitLeadOrigin || hasIncomingTrackingIds) &&
    !(isDirectOrigin && hasExistingRealOrigin);

  if (resolvedLeadOrigin && !existingLeadOriginFirst) {
    setStoredValue(LEAD_ORIGIN_FIRST_COOKIE, resolvedLeadOrigin);
  }

  if (resolvedLeadOrigin && shouldUpdateLeadOrigin) {
    setStoredValue(LEAD_ORIGIN_LAST_COOKIE, resolvedLeadOrigin);
    setStoredValue(LEAD_ORIGIN_LEGACY_COOKIE, resolvedLeadOrigin);

    const history = readLeadOriginHistoryCookie();
    const nextEntry: LeadOriginHistoryEntry = {
      origin: resolvedLeadOrigin,
      timestamp: new Date().toISOString(),
      trackingIds: mergedTrackingIds,
    };

    const lastEntry = history[history.length - 1];
    const shouldAppend =
      !lastEntry ||
      lastEntry.origin !== nextEntry.origin ||
      JSON.stringify(lastEntry.trackingIds) !==
        JSON.stringify(nextEntry.trackingIds);

    if (shouldAppend) {
      const updatedHistory = [...history, nextEntry].slice(
        -MAX_LEAD_ORIGIN_HISTORY,
      );
      setStoredValue(
        LEAD_ORIGIN_HISTORY_COOKIE,
        serializeLeadOriginHistory(updatedHistory),
      );
    }
  } else if (!existingLeadOrigin && resolvedLeadOrigin) {
    // Only seed "direct" if there's no real origin already stored
    if (!(isDirectOrigin && hasExistingRealOrigin)) {
      setStoredValue(LEAD_ORIGIN_LEGACY_COOKIE, resolvedLeadOrigin);
    }
    if (!existingLeadOriginLast) {
      setStoredValue(LEAD_ORIGIN_LAST_COOKIE, resolvedLeadOrigin);
    }

    const history = readLeadOriginHistoryCookie();
    if (history.length === 0) {
      const seedEntry: LeadOriginHistoryEntry = {
        origin: resolvedLeadOrigin,
        timestamp: new Date().toISOString(),
        trackingIds: mergedTrackingIds,
      };
      setStoredValue(
        LEAD_ORIGIN_HISTORY_COOKIE,
        serializeLeadOriginHistory([seedEntry]),
      );
    }
  }

  return {
    trackingIds: mergedTrackingIds,
    leadOrigin: resolvedLeadOrigin || null,
    leadOriginFirst: existingLeadOriginFirst || resolvedLeadOrigin || null,
    leadOriginLast:
      (shouldUpdateLeadOrigin ? resolvedLeadOrigin : existingLeadOriginLast) ||
      existingLeadOrigin ||
      resolvedLeadOrigin ||
      null,
    leadOriginHistory: readLeadOriginHistoryCookie(),
  };
};
